import React, { useEffect } from "react";
import { Box } from "@mui/material";
import QRCode from "react-qr-code";
import {
  Inventory2Outlined, PaymentsOutlined, StickyNote2Outlined,
  TimelineOutlined, DescriptionOutlined, AltRouteOutlined, RouteOutlined,
  TaskAltOutlined, SyncAltOutlined, CurrencyExchangeOutlined, HistoryOutlined,
  CategoryOutlined, SmsOutlined,
} from "@mui/icons-material";
import { useShipmentDetailContext } from "contexts/ShipmentDetailContext";
import CompactCard from "./CompactCard";
import ShipmentBasicDetailCompact from "./ShipmentBasicDetailCompact";
import ShipmentPricingCompact from "./ShipmentPricingCompact";
import ShipmentNotesCompact from "./ShipmentNotesCompact";
import ShipmentDocumentsCompact from "./ShipmentDocumentsCompact";
import ShipmentTransitBranchCompact from "./ShipmentTransitBranchCompact";
import ShipmentItemsCompact from "./ShipmentItemsCompact";
import ShipmentRoutesTab from "./ShipmentRoutesTab";
import ShipmentActionTab from "./ShipmentActionTab";
import ShipmentChangeLogsCompact from "./ShipmentChangeLogsCompact";
import ShipmentRefundCompact from "./ShipmentRefundCompact";
import ShipmentProcessCompact from "./ShipmentProcessCompact";
import ShipmentHistoryCompact from "./ShipmentHistoryCompact";
import ShipmentSmsLogCompact from "./ShipmentSmsLogCompact";
import {
  useLazyGetShipmentLogsQuery, useLazyListShipmentRoutesQuery,
} from "store/services/shipmentApi";
import { useLazyGetShipmentActionListQuery } from "store/services/shipmentActionApi";
import { useGetShipmentRefundDetailQuery } from "store/services/PaymentsApi/shipmentRefundApi";
import { useGetShipmentMessagesByShipmentIdQuery } from "store/services/smsLogListApi";
import returnImage from "../../../../../assets/statusImages/return.png";
import onHoldImage from "../../../../../assets/statusImages/onhold.png";
import deliveredImage from "../../../../../assets/statusImages/delivered.png";
import cancelledImage from "../../../../../assets/statusImages/cancelled.png";

const statusImages = { Delivered: deliveredImage, Cancelled: cancelledImage };

// Renders a 50/50 (or configurable ratio) two-column row that reflows to
// a single full-width column if only one side has data, and renders nothing if neither does.
const TwoUpRow = ({
  leftShow, leftTitle, leftIcon, leftNode, leftRatio = 1,
  rightShow, rightTitle, rightIcon, rightNode, rightRatio = 1,
  maxHeight = 340,
}) => {
  if (!leftShow && !rightShow) return null;
  const columns = leftShow && rightShow ? `${leftRatio}fr ${rightRatio}fr` : "1fr";
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: columns }, gap: 2, alignItems: "stretch" }}>
      {leftShow && (
        <CompactCard title={leftTitle} icon={leftIcon} maxHeight={maxHeight}>
          {leftNode}
        </CompactCard>
      )}
      {rightShow && (
        <CompactCard title={rightTitle} icon={rightIcon} maxHeight={maxHeight}>
          {rightNode}
        </CompactCard>
      )}
    </Box>
  );
};

const ShipmentDetailClassicView = () => {
  const { ShipmentDetail } = useShipmentDetailContext();
  const shipmentId = ShipmentDetail?._id;

  const { data: shipmentRefundDetail } = useGetShipmentRefundDetailQuery(shipmentId, { skip: !shipmentId });

  const [fetchRoutes, { data: routesRes }] = useLazyListShipmentRoutesQuery();
  const [fetchActions, { data: actionsRes }] = useLazyGetShipmentActionListQuery();
  const [fetchChangeLogs, { data: changeLogsRes }] = useLazyGetShipmentLogsQuery();

  useEffect(() => {
    if (shipmentId) {
      fetchRoutes({ shipmentId });
      fetchActions({ shipmentId });
      fetchChangeLogs(shipmentId);
    }
  }, [shipmentId, fetchRoutes, fetchActions, fetchChangeLogs]);

  const routeCount = routesRes?.totalItems ?? routesRes?.data?.length ?? 0;
  const actionCount = actionsRes?.totalItems ?? actionsRes?.data?.length ?? 0;
  const changeLogCount = changeLogsRes?.data?.details?.length ?? changeLogsRes?.totalItems ?? 0;

  const { data: smsLogs } = useGetShipmentMessagesByShipmentIdQuery(shipmentId, { skip: !shipmentId });

  const hasItems = true;
  const hasProcessing = ShipmentDetail?.shipmentProcess?.length > 0;
  const hasHistory = ShipmentDetail?.history?.length > 0;

  // Row 6 — Routes, Action, SMS Log, each independently gated on data
  const leftoverSections = [
    { key: "routes", show: routeCount > 0, title: "Shipment Routes", icon: RouteOutlined, node: <ShipmentRoutesTab /> },
    { key: "action", show: actionCount > 0, title: "Action", icon: TaskAltOutlined, node: <ShipmentActionTab /> },
    { key: "sms", show: smsLogs?.totalItems > 0, title: "SMS Log", icon: SmsOutlined, node: <ShipmentSmsLogCompact shipmentId={shipmentId} /> },
  ].filter((s) => s.show);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 2,
        borderRadius: 2,
        bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "grey.50"),
      }}
    >
      {/* Row 1: Order Details (60) | QR only, no card chrome (10) | Pricing (30) */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "6fr 1fr 3fr" }, gap: 2, alignItems: "stretch" }}>
        <CompactCard title="Order Details" icon={Inventory2Outlined} maxHeight={480}>
          <ShipmentBasicDetailCompact />
        </CompactCard>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, height: "100%" }}>
          <QRCode value={ShipmentDetail?.shipmentId || "N/A"} size={100} />
          {ShipmentDetail?.isOnHold ? (
            <img src={onHoldImage} alt="On Hold" style={{ width: 70, borderRadius: 6 }} />
          ) : ShipmentDetail?.IsReturning ? (
            <img src={returnImage} alt="Returning" style={{ width: 70, borderRadius: 6 }} />
          ) : (
            statusImages[ShipmentDetail?.status] && (
              <img src={statusImages[ShipmentDetail?.status]} alt={ShipmentDetail?.status} style={{ width: 70, borderRadius: 6 }} />
            )
          )}
        </Box>
        <CompactCard title="Pricing" icon={PaymentsOutlined} maxHeight={480}>
          <ShipmentPricingCompact />
        </CompactCard>
      </Box>

      {/* Row 2: Notes (50) | Documents (30) | Transit Branch (20) — all always shown */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "5fr 3fr 2fr" }, gap: 2, alignItems: "stretch" }}>
        <CompactCard title="Notes" icon={StickyNote2Outlined} maxHeight={340}>
          <ShipmentNotesCompact source="Shipment" sourceRef={shipmentId} isActive={ShipmentDetail?.isActive} />
        </CompactCard>
        <CompactCard title="Documents" icon={DescriptionOutlined} maxHeight={340}>
          <ShipmentDocumentsCompact source="Shipment" sourceRef={shipmentId} isActive={ShipmentDetail?.isActive} />
        </CompactCard>
        <CompactCard title="Transit Branch" icon={AltRouteOutlined} maxHeight={340}>
          <ShipmentTransitBranchCompact />
        </CompactCard>
      </Box>

      {/* Row 3: Processing (50) | History (50) */}
      <TwoUpRow
        leftShow={hasProcessing} leftTitle="Processing" leftIcon={TimelineOutlined} leftNode={<ShipmentProcessCompact />}
        rightShow={hasHistory} rightTitle="History" rightIcon={HistoryOutlined} rightNode={<ShipmentHistoryCompact />}
      />

      {/* Row 4: Items (50) | Change Log (50) */}
      <TwoUpRow
        leftShow={hasItems} leftTitle="Items" leftIcon={CategoryOutlined} leftNode={<ShipmentItemsCompact />}
        rightShow={changeLogCount > 0} rightTitle="Change Log" rightIcon={SyncAltOutlined} rightNode={<ShipmentChangeLogsCompact shipmentId={shipmentId} />}
      />

      {/* Row 5: Refund — only when available */}
      {!!shipmentRefundDetail?._id && (
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 2 }}>
          <CompactCard title="Refund" icon={CurrencyExchangeOutlined} maxHeight={420}>
            <ShipmentRefundCompact shipmentId={shipmentId} />
          </CompactCard>
        </Box>
      )}

      {/* Row 6: Routes / Action / SMS Log */}
      {leftoverSections.length > 0 && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }, gap: 2, alignItems: "stretch" }}>
          {leftoverSections.map((s) => (
            <CompactCard key={s.key} title={s.title} icon={s.icon} maxHeight={300}>
              {s.node}
            </CompactCard>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ShipmentDetailClassicView;